import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { findBy, GeotabCall, listAll } from "./assetScanStore";
import { useGeotabClient } from "@/components/GeotabClientContext";
import AddInIds from "@/constants/AddInIds";
import { ScanEvent, Stored } from "@/models/assetScan/AssetScanTypes";

export const scanListKey = (addInId: string, activeSessionId: string | null) =>
  ["assetScan", addInId, activeSessionId] as const;

export function useGeotabCall(): GeotabCall {
  const { geotabClient } = useGeotabClient();
  return (method, params, signal) => geotabClient.call(method, params, signal);
}

/** A whole list (assets, customers, event types). Not for events: those are read per asset. */
export function useScanList<T>(addInId: string, enabled = true): UseQueryResult<Stored<T>[]> {
  const { activeSessionId } = useGeotabClient();
  const call = useGeotabCall();
  return useQuery({
    queryKey: scanListKey(addInId, activeSessionId),
    queryFn: ({ signal }) => listAll<T>(call, addInId, signal),
    enabled,
  });
}

/** One asset's events, oldest first. */
export function useAssetEvents(tag?: string): UseQueryResult<Stored<ScanEvent>[]> {
  const { activeSessionId } = useGeotabClient();
  const call = useGeotabCall();
  return useQuery({
    queryKey: [...scanListKey(AddInIds.assetScanEvents, activeSessionId), tag],
    queryFn: async ({ signal }) => {
      const rows = await findBy<ScanEvent>(call, AddInIds.assetScanEvents, "asset", tag ?? "", signal);
      return rows.sort((a, b) => a.details.time.localeCompare(b.details.time));
    },
    enabled: !!tag,
  });
}
